import { LOINC } from '@medplum/core'
import { sortBy } from 'lodash-es'
import type { CodingValue } from '#src/codeableConcept.ts'
import {
  codeableConcept,
  codeFromCodeableConcept,
  displayFromCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  DOCUMENT_CATEGORY_DESCRIPTION_SYSTEM,
  DOCUMENT_CATEGORY_SYSTEM,
  DOCUMENT_SOURCE_SYSTEM,
  DOCUMENT_TYPE_SYSTEM,
} from '#src/codeSystems.ts'
import { recordEntries } from '#src/utils.ts'

export const DocumentCategory = {
  SPECIALIST_CONSULTS: 'consult',
  DISENROLLMENT: 'disenrollment',
  ENROLLMENT: 'enrollment',
  FORMS: 'forms',
  IMAGING: 'images',
  INCIDENTS: 'incidents',
  LABS: 'labs',
  LEGAL: 'legal',
  MISC: 'misc',
  RX: 'rx',
  SDR: 'sdr',
  HOME_CARE: 'home-care',
  HOSPITAL_RECORDS: 'hospital-records',
  AUTHORIZATIONS: 'authorizations',
} as const
export type DocumentCategory =
  (typeof DocumentCategory)[keyof typeof DocumentCategory]

/**
 * Normalize a set of classifier-assigned categories to the taxonomy's
 * invariants before storing them on a DocumentReference:
 * - de-duplicate, preserving first-seen order
 * - `MISC` is the "none of the others apply" fallback, so it is mutually
 *   exclusive with every real category — drop it when any real category is
 *   present.
 *
 * Defends the store against a classifier that hallucinates duplicates or
 * returns `MISC` alongside a real category despite the prompt forbidding it.
 */
export function normalizeDocumentCategories(
  categories: DocumentCategory[]
): DocumentCategory[] {
  const deduped = [...new Set(categories)]
  const real = deduped.filter((c) => c !== DocumentCategory.MISC)
  return real.length > 0 ? real : deduped
}

const documentCategoriesMap = {
  [DocumentCategory.SPECIALIST_CONSULTS]: {
    display: 'Consults',
    description:
      "Outpatient specialist visit notes and reports from external specialty providers, including: office visit notes, progress notes, SOAP notes, follow-up visit notes, consultation letters, and procedure notes from clinic-based procedures. Typical sources: cardiology, pulmonology, gastroenterology, dermatology, neurology, podiatry, optometry, audiology, ENT, dental, oncology, and similar outpatient specialty clinics. Does NOT include inpatient hospital records (those are 'hospital-records'), authorization paperwork (those are 'authorizations'), or imaging/diagnostic study reports (those are 'images').",
  },
  [DocumentCategory.DISENROLLMENT]: {
    display: 'Disenrollment',
    description:
      'Documents related to requests for, or confirmation of, disenrollment or termination from a health plan.',
  },
  [DocumentCategory.ENROLLMENT]: {
    display: 'Enrollment',
    description:
      'Documents related to patient intake, registration, and enrollment in a health plan or program.',
  },
  [DocumentCategory.FORMS]: {
    display: 'Forms',
    description:
      'Blank or completed administrative forms, such as consent forms, release of information, or intake questionnaires.',
  },
  [DocumentCategory.IMAGING]: {
    display: 'Imaging',
    description:
      "Diagnostic studies and their interpretations, including: X-rays, CT scans, MRIs, ultrasounds, mammograms, DEXA scans, nuclear medicine scans, PET scans, echocardiograms, ECGs/EKGs, stress tests, Holter monitor reports, and audiometry/hearing tests. Includes both anatomic imaging and physiologic diagnostic studies. Does NOT include lab analyses of biological specimens (those are 'labs').",
  },
  [DocumentCategory.INCIDENTS]: {
    display: 'Incidents',
    description:
      'Reports of adverse events, safety incidents, injuries, falls, or other unexpected occurrences affecting patient care.',
  },
  [DocumentCategory.LABS]: {
    display: 'Labs',
    description:
      "Results from laboratory analysis of biological specimens such as blood, urine, saliva, stool, tissue, or swabs. Includes: chemistry panels, hematology (CBC), microbiology cultures, pathology reports, serology, urinalysis, drug screens, genetic testing, and cytology. Does NOT include diagnostic imaging or physiologic studies like ECG or audiometry (those are 'images').",
  },
  [DocumentCategory.LEGAL]: {
    display: 'Legal',
    description:
      'Legal documents including power of attorney, guardianship papers, court orders, subpoenas, POLST, DNR orders, and other advance directives.',
  },
  [DocumentCategory.MISC]: {
    display: 'Miscellaneous',
    description:
      'For documents that do not fit any other category. Use only when no other category is appropriate.',
  },
  [DocumentCategory.RX]: {
    display: 'Rx',
    description:
      'Medication-related documents, including prescriptions, refill requests, and medication history summaries.',
  },
  [DocumentCategory.SDR]: {
    display: 'SDR',
    description:
      "Outbound service determination requests submitted by Seen Health to a health plan or IPA asking for approval of a service. Includes: prior authorization request forms, coverage determination request forms, and the supporting clinical documentation submitted with such requests. Does NOT include the health plan's response or approval notification — those are 'authorizations'.",
  },
  [DocumentCategory.HOME_CARE]: {
    display: 'Home Care',
    description:
      'Documents related to home care services, including caregiver schedules, in-home care notes, and home health aide reports.',
  },
  [DocumentCategory.HOSPITAL_RECORDS]: {
    display: 'Hospital',
    description:
      "Records produced during an INPATIENT hospital stay or emergency department (ED) visit. Includes: hospital admission notes, discharge summaries, ED notes, inpatient progress notes, operative reports from hospital surgical procedures, inpatient consultations, and hospital case management notes. Does NOT include outpatient clinic visit notes — an office visit at a freestanding specialist clinic is 'consult', not 'hospital-records', even if the specialist also has hospital privileges or the practice name contains 'Medical Center'. The distinguishing factor is INPATIENT setting or ED encounter, not the facility name.",
  },
  [DocumentCategory.AUTHORIZATIONS]: {
    display: 'Authorizations',
    description:
      "Referral authorization documents issued by health plans, IPAs (e.g., Astrana Health, Allied Pacific of California), or Seen Health to authorize a participant to seek specialty care from an external provider. Includes: authorization notification letters, member approval letters, provider approval letters, proof edit letters, denial notifications, and similar referral authorization response documents. Does NOT include the actual consult notes, progress notes, or visit reports returned by the specialist after the visit (those are 'consult'), and does NOT include the outbound request paperwork submitted by Seen Health asking for approval (those are 'sdr').",
  },
} as const

export const DOCUMENT_CATEGORIES = Object.fromEntries(
  Object.entries(documentCategoriesMap).map(([key, value]) => [
    key,
    {
      system: DOCUMENT_CATEGORY_SYSTEM,
      code: key,
      display: value.display,
      extension: [
        {
          url: DOCUMENT_CATEGORY_DESCRIPTION_SYSTEM,
          valueString: value.description,
        },
      ],
    },
  ])
)

export const DOCUMENT_CATEGORY_OPTIONS: {
  value: DocumentCategory
  label: string
}[] = sortBy(
  recordEntries(DOCUMENT_CATEGORIES)
    // Forms is only used for elation syncing
    .filter(([key]) => key !== DocumentCategory.FORMS)
    .map(([key, value]) => ({
      value: key as DocumentCategory,
      label: value.display ?? key,
    })),
  'label'
)

export const DocumentTypes = {
  OrderPDF: 'order-pdf',
  Desk: 'desk',
  CanvasArtifact: 'orbit-canvas-artifact',
  CanvasThreadLogPage: 'orbit-canvas-thread-log-page',
  MEDICAID_ID: 'medicaid-id',
  MEDICARE_ID: 'medicare-id',
  POLST: 'polst',
  ADVANCE_DIRECTIVE: 'advance-directive',
  FALL_REPORT: 'fall-report',
  APPEAL: 'appeal',
} as const
export type DocumentTypes = (typeof DocumentTypes)[keyof typeof DocumentTypes]

export const APPLICATION_DOCUMENT_TYPES = [
  DocumentTypes.Desk,
  DocumentTypes.CanvasArtifact,
  DocumentTypes.CanvasThreadLogPage,
] as const

// Fixed title for POLST documents. These forms encode life-sustaining-treatment
// decisions in checkboxes and handwriting that an OCR model can misread, so their
// title is a constant assembled in code — never composed from the document
// contents — to keep treatment choices (DNR/DNI/etc.) out of the displayed title
// and summary.
export const POLST_TITLE =
  'POLST: Physician Orders for Life-sustaining Treatment'

// Identifies a document as an actual POLST order form by its title or
// attachment title. The 'polst' document type is the shared "POLST / Advanced
// Directive" bucket, so it also holds Advance Health Care Directives — this
// pattern (which excludes generic "advance directive" wording) separates real
// POLSTs from AHCDs so only POLSTs get the fixed POLST title.
export const POLST_FORM_PATTERN =
  /\bpolst\b|physician orders for life[- ]sustaining|\bmolst\b/i

// Matches the treatment-choice language a misread POLST leaks into a title or
// attachment title (the exact text this backfill exists to scrub). Used to
// detect leaked attachment titles that must be reset alongside the description.
export const POLST_LEAK_PATTERN =
  /\bDNR\b|\bDNI\b|do not resuscitate|do not intubate|comfort[- ]focused|selective treatment|full treatment|artificial nutrition|attempt CPR|\bCPR\b/i

// Deterministic title/type for a POLST document. Fires when the model flags the
// document as a POLST or the document is already typed POLST. Returns null for
// everything else so callers fall through to normal AI title/summary handling.
// Callers additionally clear the AI summary when this returns non-null.
export function polstPresentation(input: {
  isPolst: boolean
  currentTypeCode?: DocumentTypes
}): { description: string; typeCode: DocumentTypes } | null {
  if (!input.isPolst && input.currentTypeCode !== DocumentTypes.POLST) {
    return null
  }
  return { description: POLST_TITLE, typeCode: DocumentTypes.POLST }
}

// Fixed title for Advance Health Care Directives (AHCDs — California living-will /
// healthcare-proxy forms). Like POLST, these encode care preferences and agent
// designations in checkboxes and handwriting an OCR model can misread, so their
// title is a constant assembled in code — never composed from the document
// contents — to keep those decisions out of the displayed title and summary.
export const AHCD_TITLE = 'AHCD: Advance Health Care Directive'

// Identifies a document as an actual Advance Health Care Directive by its title
// or attachment title. Kept deliberately DISJOINT from POLST_FORM_PATTERN (which
// keys on polst/physician-orders/molst) so a real POLST is never caught here and
// vice versa — the two form classes must never cross-match.
export const AHCD_FORM_PATTERN =
  /advance health care directive|\bahcd\b|prepare for your care|living will|health ?care (agent|proxy|surrogate)|durable power of attorney for health/i

// Matches the care-preference language a misread AHCD leaks into a title or
// attachment title (e.g. "no intubation", "refuses life support",
// "comfort-focused", "DNR"). Used to detect leaked attachment titles that must
// be reset alongside the description. Deliberately excludes bare form-name and
// agent-designation words (agent/proxy/surrogate) so clean upload filenames like
// "Health Care Proxy.pdf" or "Healthcare Agent Designation.pdf" are left
// untouched — a real leak that names an agent carries this care-preference
// co-text too (e.g. "agent daughter, refuses life support").
export const AHCD_LEAK_PATTERN =
  /no intubation|do not intubate|\bDNI\b|do not resuscitate|\bDNR\b|full code|life support|life[- ]sustaining|comfort[- ]?focused|comfort measures|resuscitat|artificial nutrition|feeding tube/i

// Deterministic title/type for an Advance Health Care Directive. Fires when the
// model flags the document as an AHCD or the document is already typed
// advance-directive. Returns null for everything else so callers fall through to
// normal AI title/summary handling. Callers additionally clear the AI summary
// when this returns non-null. Mirrors polstPresentation.
export function advanceDirectivePresentation(input: {
  isAdvanceDirective: boolean
  currentTypeCode?: DocumentTypes
}): { description: string; typeCode: DocumentTypes } | null {
  if (
    !input.isAdvanceDirective &&
    input.currentTypeCode !== DocumentTypes.ADVANCE_DIRECTIVE
  ) {
    return null
  }
  return { description: AHCD_TITLE, typeCode: DocumentTypes.ADVANCE_DIRECTIVE }
}

export const DOCUMENT_TYPES = {
  [DocumentTypes.CanvasArtifact]: codeableConcept({
    system: DOCUMENT_TYPE_SYSTEM,
    code: DocumentTypes.CanvasArtifact,
    display: 'Orbit Canvas artifact',
  }),
  [DocumentTypes.CanvasThreadLogPage]: codeableConcept({
    system: DOCUMENT_TYPE_SYSTEM,
    code: DocumentTypes.CanvasThreadLogPage,
    display: 'Orbit Canvas thread log page',
  }),
  [DocumentTypes.OrderPDF]: codeableConcept({
    system: DOCUMENT_TYPE_SYSTEM,
    code: DocumentTypes.OrderPDF,
    display: 'Order PDF',
  }),
  [DocumentTypes.Desk]: codeableConcept({
    system: DOCUMENT_TYPE_SYSTEM,
    code: DocumentTypes.Desk,
    display: 'Desk',
  }),
  [DocumentTypes.MEDICAID_ID]: codeableConcept([
    {
      system: DOCUMENT_TYPE_SYSTEM,
      code: DocumentTypes.MEDICAID_ID,
      display: 'Medicaid ID',
    },
    {
      // https://loinc.org/LP173404-7
      system: LOINC,
      code: 'LP173404-7',
      display: 'Health Insurance Card',
    },
  ]),
  [DocumentTypes.MEDICARE_ID]: codeableConcept([
    {
      system: DOCUMENT_TYPE_SYSTEM,
      code: DocumentTypes.MEDICARE_ID,
      display: 'Medicare ID',
    },
    {
      // https://loinc.org/LP173404-7
      system: LOINC,
      code: 'LP173404-7',
      display: 'Health Insurance Card',
    },
  ]),
  [DocumentTypes.POLST]: codeableConcept({
    system: DOCUMENT_TYPE_SYSTEM,
    code: DocumentTypes.POLST,
    display: 'POLST / Advanced Directive',
  }),
  [DocumentTypes.ADVANCE_DIRECTIVE]: codeableConcept({
    system: DOCUMENT_TYPE_SYSTEM,
    code: DocumentTypes.ADVANCE_DIRECTIVE,
    display: 'Advance Health Care Directive',
  }),
  [DocumentTypes.FALL_REPORT]: codeableConcept({
    system: DOCUMENT_TYPE_SYSTEM,
    code: DocumentTypes.FALL_REPORT,
    display: 'Fall Report',
  }),
  [DocumentTypes.APPEAL]: codeableConcept({
    system: DOCUMENT_TYPE_SYSTEM,
    code: DocumentTypes.APPEAL,
    display: 'Appeal',
  }),
}

export const DOCUMENT_TYPE_OPTIONS_BY_CATEGORY = {
  [DocumentCategory.ENROLLMENT]: sortBy(
    [DocumentTypes.MEDICAID_ID, DocumentTypes.MEDICARE_ID].map((type) => ({
      value: codeFromCodeableConcept(DOCUMENT_TYPES[type]) ?? '',
      label: displayFromCodeableConcept(DOCUMENT_TYPES[type]) ?? '',
    })),
    'label'
  ),
  [DocumentCategory.LEGAL]: sortBy(
    [DocumentTypes.POLST, DocumentTypes.ADVANCE_DIRECTIVE].map((type) => ({
      value: codeFromCodeableConcept(DOCUMENT_TYPES[type]) ?? '',
      label: displayFromCodeableConcept(DOCUMENT_TYPES[type]) ?? '',
    })),
    'label'
  ),
  [DocumentCategory.INCIDENTS]: [DocumentTypes.FALL_REPORT].map((type) => ({
    value: codeFromCodeableConcept(DOCUMENT_TYPES[type]) ?? '',
    label: displayFromCodeableConcept(DOCUMENT_TYPES[type]) ?? '',
  })),
  [DocumentCategory.MISC]: [DocumentTypes.APPEAL].map((type) => ({
    value: codeFromCodeableConcept(DOCUMENT_TYPES[type]) ?? '',
    label: displayFromCodeableConcept(DOCUMENT_TYPES[type]) ?? '',
  })),
} as Partial<Record<DocumentCategory, { value: string; label: string }[]>>

export const DocumentSource = {
  SINCH_WEBHOOK: 'sinch_webhook',
  ELATION_INCOMING_FILES: 'elation_incoming_files',
  BULK_UPLOAD: 'bulk_upload',
  AI_CHAT: 'ai_chat',
  INLINE_PASTE: 'inline_paste',
  OTHER: 'other',
} as const
export type DocumentSource =
  (typeof DocumentSource)[keyof typeof DocumentSource]

export const InlinePasteProcessingStatus = {
  PENDING: 'pending',
  COMPLETE: 'complete',
  NO_TEXT: 'no-text',
} as const
export type InlinePasteProcessingStatus =
  (typeof InlinePasteProcessingStatus)[keyof typeof InlinePasteProcessingStatus]

export const DocumentTranscriptionOutcome = {
  FAILED: 'failed',
  NO_TEXT: 'no-text',
} as const
export type DocumentTranscriptionOutcome =
  (typeof DocumentTranscriptionOutcome)[keyof typeof DocumentTranscriptionOutcome]

export const DOCUMENT_SOURCES = {
  [DocumentSource.SINCH_WEBHOOK]: {
    system: DOCUMENT_SOURCE_SYSTEM,
    code: DocumentSource.SINCH_WEBHOOK,
    display: 'Virtual Fax',
  },
  [DocumentSource.ELATION_INCOMING_FILES]: {
    system: DOCUMENT_SOURCE_SYSTEM,
    code: DocumentSource.ELATION_INCOMING_FILES,
    display: 'Elation Fax Inbox',
  },
  [DocumentSource.BULK_UPLOAD]: {
    system: DOCUMENT_SOURCE_SYSTEM,
    code: DocumentSource.BULK_UPLOAD,
    display: 'Bulk Upload',
  },
  [DocumentSource.AI_CHAT]: {
    system: DOCUMENT_SOURCE_SYSTEM,
    code: DocumentSource.AI_CHAT,
    display: 'AI Chat',
  },
  [DocumentSource.INLINE_PASTE]: {
    system: DOCUMENT_SOURCE_SYSTEM,
    code: DocumentSource.INLINE_PASTE,
    display: 'Pasted in Editor',
  },
  [DocumentSource.OTHER]: {
    system: DOCUMENT_SOURCE_SYSTEM,
    code: DocumentSource.OTHER,
    display: 'Other',
  },
} as const satisfies Record<DocumentSource, CodingValue>

export const DOCUMENT_SOURCE_OPTIONS: {
  value: DocumentSource
  label: string
}[] = sortBy(
  recordEntries(DOCUMENT_SOURCES).map(([key, value]) => ({
    value: key as DocumentSource,
    label: value.display ?? key,
  })),
  'label'
)

export type DocumentSourceCode = DocumentSource

export const DOCUMENT_CATEGORY_COLORS: Record<DocumentCategory, string> = {
  [DocumentCategory.SPECIALIST_CONSULTS]: '#5e6ad2', // Purple
  [DocumentCategory.DISENROLLMENT]: '#95a2b3', // Dark Gray
  [DocumentCategory.ENROLLMENT]: '#4cb782', // Green
  [DocumentCategory.FORMS]: '#bec2c8', // Gray
  [DocumentCategory.IMAGING]: '#26b5ce', // Teal
  [DocumentCategory.INCIDENTS]: '#eb5757', // Red
  [DocumentCategory.LABS]: '#f2c94c', // Yellow
  [DocumentCategory.LEGAL]: '#f2994a', // Orange
  [DocumentCategory.MISC]: '#bec2c8', // Gray
  [DocumentCategory.RX]: '#f7c8c1', // Pink
  [DocumentCategory.SDR]: '#5e6ad2', // Purple
  [DocumentCategory.HOME_CARE]: '#4cb782', // Green
  [DocumentCategory.HOSPITAL_RECORDS]: '#26b5ce', // Teal
  [DocumentCategory.AUTHORIZATIONS]: '#9b59b6', // Purple
}

// Internal-only audit categories. DocumentReferences using these are tagged
// DOCUMENT_VISIBILITY_SYSTEM_INTERNAL and excluded from UI search; the coding
// exists solely to satisfy FHIR's `category` requirement and must not appear
// in user-facing pickers.
export const INTERNAL_DOCUMENT_CATEGORY_GRANERX_HL7 = {
  system: DOCUMENT_CATEGORY_SYSTEM,
  code: 'granerx-hl7',
  display: 'GraneRx HL7 (internal)',
} as const satisfies CodingValue
